import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EditProjectModel } from '@app/components/company/company-projects/edit-project-dialog/edit-project-form-controls';
import { ProjectDTO } from '@app/shared/services/dtos/project/project.dto';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectIndustriesDataService } from '@app/shared/services/project-industries-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { UpdateProjectDTO } from '@app/shared/types/models/project/dto/update-project-dto';
import { Project } from '@app/shared/types/models/project/project.model';
import { Observable } from 'rxjs';

/**
 * A field the project did not have (`null`/`undefined`) and the user left empty keeps its original value, so the
 * request is the same as the one built from the reactive form value before (absent stays absent).
 */
const keepMissing = <T>(current: T, original: T | null | undefined, empty: T): T | null | undefined =>
  current === empty && original == null ? original : current;

@Injectable()
export class EditProjectService {
  private readonly locationService: LocationsDataService = inject(LocationsDataService);
  private readonly projectService: ProjectsDataService = inject(ProjectsDataService);
  private readonly projectIndustriesService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public createInitialModel(project: Project): EditProjectModel {
    return {
      id: project.id,
      name: project.name ?? '',
      avatarId: project.avatarId ?? '',
      description: project.description ?? '',
      supervisor: project.supervisor() ?? null,
      client: project.client() ?? null,
      startDate: project.startDate ?? null,
      endDate: project.endDate ?? null,
      estimation: {
        financial: {
          amount: project.estimation?.financial.amount ?? null,
          currency: project.estimation?.financial.currency ?? null
        },
        time: {
          amount: project.estimation?.hours ?? null,
          units: TimeUnits.HOURS
        }
      },
      address: project.address ?? ''
    };
  }

  public saveProject(model: EditProjectModel, project: Project, isDraftList: boolean): Observable<ProjectDTO> {
    const { financial, time } = model.estimation;

    const projectDTO: UpdateProjectDTO = {
      id: model.id,
      address: keepMissing(model.address, project.address, ''),
      avatarId: keepMissing(model.avatarId, project.avatarId, ''),

      // temporary disabled
      // clientId: formValue.client.id,

      description: keepMissing(model.description, project.description, ''),
      endDate: keepMissing(model.endDate, project.endDate, null),
      estimation: {
        hours: Number(keepMissing(time.amount, project.estimation?.hours, null)),
        financial: {
          amount: Number(keepMissing(financial.amount, project.estimation?.financial.amount, null)),
          currency: keepMissing(financial.currency, project.estimation?.financial.currency, null)
        }
      },
      name: model.name,
      startDate: keepMissing(model.startDate, project.startDate, null),
      supervisorId: model.supervisor?.id
    };
    return this.projectService.updateProject(projectDTO, isDraftList);
  }

  public initProjectData(projectId: string): void {
    this.projectIndustriesService.loadProjectIndustries(projectId);
    this.locationService.loadProjectLocations(projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
